import { useMemo, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { MarketPayload, TimeGrain, TrendPoint } from "../shared/market";
import { snapshot, type ModelEvent, type Preset, type WeeklyAnalysis } from "./generated/snapshot";
import { layoutModelEvents, rangeAnalysis, shiftDay, viewForPreset, viewForRange } from "./market-view";

type RangeMode = Preset | "custom";
type ChartMetric = "tokens" | "price" | "value";

const DASH = "—";
const PRESET_LABELS: Record<Preset, string> = { "30d": "30 天", "90d": "90 天", "1y": "1 年", all: "全部" };
const GRAIN_LABELS: Record<TimeGrain, string> = { day: "日", week: "周", month: "月", quarter: "季度" };

function lastIndexWhere<T>(items: T[], predicate: (item: T) => boolean): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return index;
  }
  return -1;
}

function formatCompact(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat("zh-CN", { notation: "compact", maximumFractionDigits: digits }).format(value);
}

function formatCurrency(value: number | null, compact = false): string {
  if (value === null || !Number.isFinite(value)) return DASH;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: value < 10 && !compact ? 2 : 1,
  }).format(value);
}

function formatPrice(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return DASH;
  return `$${value < 1 ? value.toFixed(2) : value.toFixed(2)}`;
}

function formatPercent(value: number | null, signed = true): string {
  if (value === null || !Number.isFinite(value)) return DASH;
  return `${signed && value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function formatDate(value: string | null, includeTime = false): string {
  if (!value) return "等待更新";
  const normalized = value.length === 10 ? `${value}T00:00:00Z` : value;
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: includeTime ? "2-digit" : undefined,
    minute: includeTime ? "2-digit" : undefined,
    timeZone: "Asia/Shanghai",
  }).format(new Date(normalized));
}

function metricValue(point: TrendPoint, metric: ChartMetric): number | null {
  if (metric === "tokens") return point.totalTokens;
  if (metric === "price") return point.blendedPricePerMillion;
  return point.estimatedRetailValue;
}

function chartValue(value: number | null, metric: ChartMetric): string {
  if (value === null) return DASH;
  if (metric === "tokens") return formatCompact(value);
  if (metric === "price") return `${formatPrice(value)} / 1M`;
  return formatCurrency(value, true);
}

function Delta({ value }: { value: number | null }) {
  const className = value === null ? "delta neutral" : value >= 0 ? "delta positive" : "delta negative";
  return <span className={className}>{formatPercent(value)} <small>vs. 前一等长区间</small></span>;
}

const CHARTS: Record<ChartMetric, {
  code: string;
  title: string;
  description: string;
  color: string;
  total: keyof MarketPayload["headline"];
  delta: keyof MarketPayload["headline"];
}> = {
  tokens: {
    code: "01 · DEMAND",
    title: "Token 消耗趋势",
    description: "公开路由市场样本中的每日 Token 总量。",
    color: "#b95737",
    total: "tokenTotal",
    delta: "tokenDeltaPct",
  },
  price: {
    code: "02 · RETAIL PRICE",
    title: "加权零售价格趋势",
    description: "按当期模型使用结构加权的公开牌价，美元 / 百万 Token。",
    color: "#c0832e",
    total: "blendedPricePerMillion",
    delta: "priceDeltaPct",
  },
  value: {
    code: "03 · VALUE SCENARIO",
    title: "估算金额趋势",
    description: "可定价 Token × 公开牌价；用于趋势观察，不代表平台流水。",
    color: "#61724b",
    total: "estimatedRetailValue",
    delta: "valueDeltaPct",
  },
};

function Brand() {
  return (
    <a className="brand" href="#top" aria-label="Compute Atlas 首页">
      <span className="brand-mark" aria-hidden="true"><i /><i /><i /></span>
      <span><strong>COMPUTE ATLAS</strong><small>AI 算力需求与价格观察</small></span>
    </a>
  );
}

function AnalysisPanel({ analysis, mode }: { analysis: WeeklyAnalysis; mode: RangeMode }) {
  return (
    <section className="analysis-panel">
      <div className="analysis-topline">
        <span className="analysis-badge">{mode === "custom" ? "即时区间事实" : "AI 周度解读"}</span>
        <span>{analysis.eyebrow}</span>
        <time>生成于 {formatDate(analysis.generatedAt, true)}</time>
      </div>
      <div className="analysis-lead">
        <span className="analysis-index">WEEKLY<br />SIGNAL</span>
        <div>
          <h2>{analysis.headline}</h2>
          <p>{analysis.summary}</p>
        </div>
      </div>
      <div className="analysis-evidence">
        {analysis.evidence.map((item, index) => (
          <article key={item}><span>0{index + 1}</span><p>{item}</p></article>
        ))}
        <article className="event-reading"><span>EVENT</span><p>{analysis.eventReading}</p></article>
      </div>
      <p className="analysis-caveat"><span>边界</span>{analysis.caveat}</p>
    </section>
  );
}

function KpiCard({ label, value, delta, note, tone }: {
  label: string;
  value: string;
  delta: number | null;
  note: string;
  tone: ChartMetric;
}) {
  return (
    <article className={`kpi-card tone-${tone}`}>
      <div className="kpi-label"><span>{label}</span><i aria-hidden="true" /></div>
      <strong>{value}</strong>
      <div className="kpi-bottom"><Delta value={delta} /><span>{note}</span></div>
    </article>
  );
}

function ChartRangeControls({ chartTitle, mode, selectedStart, selectedEnd, availableStart, availableEnd, onSelectPreset, onSelectDates }: {
  chartTitle: string;
  mode: RangeMode;
  selectedStart: string;
  selectedEnd: string;
  availableStart: string;
  availableEnd: string;
  onSelectPreset: (preset: Preset) => void;
  onSelectDates: (start: string, end: string) => void;
}) {
  return (
    <div className="chart-range-controls" aria-label={`${chartTitle}时间范围`}>
      <div className="chart-range-caption"><span>TIME RANGE</span><strong>{selectedStart} — {selectedEnd}</strong></div>
      <div className="chart-range-presets">
        {(["30d", "90d", "1y", "all"] as Preset[]).map((preset) => (
          <button key={preset} type="button" aria-pressed={mode === preset} className={mode === preset ? "active" : ""} onClick={() => onSelectPreset(preset)}>{PRESET_LABELS[preset]}</button>
        ))}
      </div>
      <div className="chart-range-dates">
        <label><span>开始</span><input aria-label={`${chartTitle}开始日期`} type="date" min={availableStart} max={selectedEnd} value={selectedStart} onInput={(event) => onSelectDates(event.currentTarget.value, selectedEnd)} onChange={(event) => onSelectDates(event.target.value, selectedEnd)} /></label>
        <i aria-hidden="true">→</i>
        <label><span>结束</span><input aria-label={`${chartTitle}结束日期`} type="date" min={selectedStart} max={availableEnd} value={selectedEnd} onInput={(event) => onSelectDates(selectedStart, event.currentTarget.value)} onChange={(event) => onSelectDates(selectedStart, event.target.value)} /></label>
      </div>
    </div>
  );
}

function TrendChart({ data, metric, events, hoveredKey, onHover, mode, selectedStart, selectedEnd, availableStart, availableEnd, onSelectPreset, onSelectDates }: {
  data: MarketPayload;
  metric: ChartMetric;
  events: ModelEvent[];
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
  mode: RangeMode;
  selectedStart: string;
  selectedEnd: string;
  availableStart: string;
  availableEnd: string;
  onSelectPreset: (preset: Preset) => void;
  onSelectDates: (start: string, end: string) => void;
}) {
  const config = CHARTS[metric];
  const width = 1200;
  const height = 286;
  const values = data.trends.map((point) => metricValue(point, metric));
  const valid = values.filter((value): value is number => value !== null && Number.isFinite(value));
  const low = valid.length ? Math.min(...valid) : 0;
  const high = valid.length ? Math.max(...valid) : 1;
  const padding = Math.max((high - low) * .14, high * .02, .000001);
  const min = metric === "price" ? low - padding : Math.max(0, low - padding);
  const max = high + padding;
  const spread = Math.max(max - min, .000001);
  const coords = data.trends.flatMap((point, index) => {
    const value = metricValue(point, metric);
    if (value === null) return [];
    const x = data.trends.length === 1 ? width / 2 : index / (data.trends.length - 1) * width;
    const y = height - (value - min) / spread * height;
    return [{ point, value, x, y }];
  });
  const path = coords.map(({ x, y }, index) => `${index ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  const active = coords.find(({ point }) => point.key === hoveredKey) ?? null;
  const labels = [0, .25, .5, .75, 1].map((ratio) => data.trends[Math.min(data.trends.length - 1, Math.round((data.trends.length - 1) * ratio))]).filter((point, index, points) => point && points.findIndex((candidate) => candidate?.key === point.key) === index);
  const rangeStart = data.meta.rangeStart ?? data.trends.at(0)?.startDate ?? "";
  const rangeEnd = data.meta.rangeEnd ?? data.trends.at(-1)?.endDate ?? "";
  const positionedEvents = layoutModelEvents(events, rangeStart, rangeEnd);
  const total = data.headline[config.total] as number | null;
  const displayTotal = metric === "tokens" ? formatCompact(total) : metric === "price" ? `${formatPrice(total)} / 1M` : formatCurrency(total, true);

  function handlePointer(event: ReactPointerEvent<SVGSVGElement>) {
    if (data.trends.length === 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const index = Math.round(ratio * (data.trends.length - 1));
    onHover(data.trends[index]?.key ?? null);
  }

  return (
    <section className="chart-panel">
      <header className="chart-header">
        <div><span className="section-code" style={{ color: config.color }}>{config.code}</span><h2>{config.title}</h2><p>{config.description}</p></div>
        <div className="chart-summary"><strong>{displayTotal}</strong><Delta value={data.headline[config.delta] as number | null} /></div>
      </header>
      <ChartRangeControls chartTitle={config.title} mode={mode} selectedStart={selectedStart} selectedEnd={selectedEnd} availableStart={availableStart} availableEnd={availableEnd} onSelectPreset={onSelectPreset} onSelectDates={onSelectDates} />
      <div className="chart-body">
        <div className="y-axis">{[1, .75, .5, .25, 0].map((ratio) => <span key={ratio}>{chartValue(min + spread * ratio, metric)}</span>)}</div>
        <div className="chart-canvas">
          <div className="chart-event-band" aria-label={`${config.title}区间内的重要模型发布`}>
            {positionedEvents.filter((item) => item.showLabel).map(({ event, position, lane }) => (
              <a key={event.id} href={event.sourceUrl} target="_blank" rel="noreferrer" className={`chart-event-label ${event.importance}${position < 0.06 ? " edge-start" : position > 0.94 ? " edge-end" : ""}`} style={{ left: `${position * 100}%`, top: `${lane * 31}px` }} aria-label={`${event.model}，${event.date}发布，查看官方来源`}>
                <strong>{event.model}</strong><time>{event.date}</time>
              </a>
            ))}
            {positionedEvents.length === 0 && <span className="chart-event-empty">当前区间没有已收录的重大模型发布</span>}
          </div>
          <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${config.title}，${rangeStart} 至 ${rangeEnd}`} onPointerMove={handlePointer} onPointerLeave={() => onHover(null)}>
            <defs>
              <linearGradient id={`fill-${metric}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={config.color} stopOpacity=".24" /><stop offset="1" stopColor={config.color} stopOpacity="0" /></linearGradient>
            </defs>
            {[0, .25, .5, .75, 1].map((ratio) => <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="chart-gridline" />)}
            {positionedEvents.map(({ event, position }) => {
              const x = position * width;
              return <g key={event.id} className={`event-rule ${event.importance}`}><line x1={x} x2={x} y1="0" y2={height} /><circle cx={x} cy="0" r={event.importance === "major" ? 4 : 3}><title>{`${event.date} · ${event.title}`}</title></circle></g>;
            })}
            {path && <><path d={`${path} L${coords.at(-1)?.x ?? width} ${height} L${coords.at(0)?.x ?? 0} ${height} Z`} fill={`url(#fill-${metric})`} /><path d={path} fill="none" stroke={config.color} strokeWidth="2.25" vectorEffect="non-scaling-stroke" /></>}
            {active && <g className="active-crosshair"><line x1={active.x} x2={active.x} y1="0" y2={height} /><circle cx={active.x} cy={active.y} r="5" fill={config.color} /></g>}
          </svg>
          {active && (
            <div className="chart-tooltip" style={{ left: `${active.x / width * 100}%` }}>
              <time>{active.point.startDate === active.point.endDate ? active.point.startDate : `${active.point.startDate} — ${active.point.endDate}`}</time>
              <strong>{chartValue(active.value, metric)}</strong>
              <span>可定价覆盖 {(active.point.pricedTokenShare * 100).toFixed(1)}%</span>
            </div>
          )}
          <div className="x-axis">{labels.map((point) => <span key={point.key}>{point.label}</span>)}</div>
          <div className="chart-event-mobile" aria-label="当前区间模型发布">
            {positionedEvents.map(({ event }) => <a key={event.id} href={event.sourceUrl} target="_blank" rel="noreferrer"><strong>{event.model}</strong><time>{event.date.slice(5)}</time></a>)}
          </div>
        </div>
      </div>
      <footer className="chart-footer"><span>{data.trends.length} 个{GRAIN_LABELS[data.meta.grain]}度统计点</span><span>三张图共享悬停日期</span></footer>
    </section>
  );
}

function RangeNavigator({ points, startIndex, endIndex, onChange }: {
  points: TrendPoint[];
  startIndex: number;
  endIndex: number;
  onChange: (start: number, end: number) => void;
}) {
  const width = 1200;
  const height = 54;
  const maxValue = Math.max(...points.map((point) => point.totalTokens), 1);
  const path = points.map((point, index) => {
    const x = index / Math.max(1, points.length - 1) * width;
    const y = height - point.totalTokens / maxValue * height;
    return `${index ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
  const left = startIndex / Math.max(1, points.length - 1) * 100;
  const right = endIndex / Math.max(1, points.length - 1) * 100;
  return (
    <section className="navigator" aria-label="图表时间窗口">
      <div className="navigator-copy"><span className="section-code">TIME WINDOW</span><strong>拖动两端选择图表区间</strong><small>所有趋势图与即时结论会同步更新</small></div>
      <div className="navigator-main">
        <div className="navigator-track">
          <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true"><path d={`${path} L${width} ${height} L0 ${height} Z`} /></svg>
          <div className="navigator-selection" style={{ left: `${left}%`, width: `${Math.max(0, right - left)}%` }} />
        </div>
        <div className="navigator-handles">
          <label><span>起点</span><input aria-label="开始日期" type="range" min="0" max={points.length - 2} value={startIndex} onChange={(event) => onChange(Math.min(Number(event.target.value), endIndex - 1), endIndex)} /></label>
          <label><span>终点</span><input aria-label="结束日期" type="range" min="1" max={points.length - 1} value={endIndex} onChange={(event) => onChange(startIndex, Math.max(Number(event.target.value), startIndex + 1))} /></label>
        </div>
        <div className="navigator-dates"><time>{points[startIndex]?.startDate}</time><span>{endIndex - startIndex + 1} 天</span><time>{points[endIndex]?.endDate}</time></div>
      </div>
    </section>
  );
}

function EventRail({ events, start, end }: { events: ModelEvent[]; start: string; end: string }) {
  const visible = events.filter((event) => event.date >= start && event.date <= end);
  return (
    <section className="event-rail">
      <div className="event-rail-title"><span className="section-code">MODEL RELEASES</span><strong>重大模型发布时间线</strong><small>{visible.length} 个事件与当前区间重叠</small></div>
      <div className="event-list">
        {visible.length ? visible.map((event) => (
          <a key={event.id} href={event.sourceUrl} target="_blank" rel="noreferrer" className={event.importance}>
            <time>{event.date.slice(2).replaceAll("-", ".")}</time><span>{event.provider}</span><strong>{event.model}</strong><i aria-hidden="true">↗</i>
          </a>
        )) : <p>当前区间没有收录的重大模型发布事件。</p>}
      </div>
    </section>
  );
}

function ModelTable({ data, disabled }: { data: MarketPayload; disabled: boolean }) {
  return (
    <section className="table-panel">
      <header className="table-header"><div><span className="section-code">04 · MODEL MIX</span><h2>模型结构明细</h2></div><span>{disabled ? "自选区间暂不提供模型拆分" : `当前区间 · Top ${Math.min(data.modelShares.length, 12)}`}</span></header>
      {disabled ? <div className="table-empty"><strong>趋势可自由选择，模型明细按预生成区间核算</strong><p>切换到 30 天、90 天、1 年或全部历史，即可查看对应模型份额与价格结构。</p></div> : (
        <div className="table-scroll"><table><thead><tr><th>模型</th><th>Token</th><th>份额</th><th>份额变化</th><th>输入价 / 1M</th><th>输出价 / 1M</th><th>加权价 / 1M</th><th>估算金额</th></tr></thead>
          <tbody>{data.modelShares.slice(0, 12).map((item) => <tr key={item.modelSlug}><td><strong>{item.name}</strong><span>{item.modelSlug}</span></td><td>{formatCompact(item.tokens)}</td><td>{formatPercent(item.share * 100, false)}</td><td className={item.shareDeltaPctPoints >= 0 ? "cell-positive" : "cell-negative"}>{item.shareDeltaPctPoints >= 0 ? "+" : ""}{item.shareDeltaPctPoints.toFixed(1)}pp</td><td>{formatPrice(item.promptPricePerMillion)}</td><td>{formatPrice(item.completionPricePerMillion)}</td><td>{formatPrice(item.blendedPricePerMillion)}</td><td>{formatCurrency(item.estimatedRetailValue, true)}</td></tr>)}</tbody>
        </table></div>
      )}
    </section>
  );
}

export default function App() {
  const allDaily = snapshot.views.all.trends;
  const latestIndex = allDaily.length - 1;
  const initialStart = Math.max(0, latestIndex - 89);
  const [grain, setGrain] = useState<TimeGrain>("day");
  const [mode, setMode] = useState<RangeMode>("90d");
  const [startIndex, setStartIndex] = useState(initialStart);
  const [endIndex, setEndIndex] = useState(latestIndex);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const availableStart = allDaily.at(0)?.startDate ?? "2025-01-01";
  const availableEnd = allDaily.at(-1)?.endDate ?? "2026-07-30";
  const selectedStart = allDaily[startIndex]?.startDate ?? availableStart;
  const selectedEnd = allDaily[endIndex]?.endDate ?? availableEnd;

  const data = useMemo(() => mode === "custom"
    ? viewForRange(snapshot.views.all, selectedStart, selectedEnd, grain)
    : viewForPreset(snapshot.views[mode], grain), [grain, mode, selectedEnd, selectedStart]);
  const analysis = mode === "custom" ? rangeAnalysis(data, snapshot.events) : snapshot.analyses[mode];
  const rangeStart = data.meta.rangeStart ?? selectedStart;
  const rangeEnd = data.meta.rangeEnd ?? selectedEnd;

  function selectPreset(preset: Preset) {
    const view = snapshot.views[preset];
    const first = allDaily.findIndex((point) => point.startDate >= (view.meta.rangeStart ?? availableStart));
    const last = lastIndexWhere(allDaily, (point) => point.endDate <= (view.meta.rangeEnd ?? availableEnd));
    setStartIndex(Math.max(0, first));
    setEndIndex(last >= 0 ? last : latestIndex);
    setMode(preset);
  }

  function selectIndexes(start: number, end: number) {
    setStartIndex(start);
    setEndIndex(end);
    setMode("custom");
  }

  function selectDates(start: string, end: string) {
    const first = allDaily.findIndex((point) => point.startDate >= start);
    const last = lastIndexWhere(allDaily, (point) => point.endDate <= end);
    if (first >= 0 && last >= first) selectIndexes(first, last);
  }

  return (
    <div className="app-shell" id="top">
      <header className="site-header">
        <Brand />
        <div className="snapshot-status"><span className="status-dot" /><span>静态快照</span><time>数据截至 {formatDate(snapshot.sourceUpdatedAt)}</time></div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy"><span className="page-kicker">PUBLIC AI COMPUTE MARKET · WEEKLY RESEARCH</span><h1>AI Token 市场<br /><em>需求、价格与金额</em></h1><p>用一条共享时间轴，观察公开样本中的算力需求、模型零售价格与估算金额。</p></div>
          <div className="hero-meta"><span>当前观察区间</span><strong>{formatDate(rangeStart)}<i>→</i>{formatDate(rangeEnd)}</strong><small>每周日 20:00（北京时间）生成新快照与解读</small></div>
        </section>

        <section className="control-bar" aria-label="统计控制">
          <div className="control-set"><span>统计粒度</span><div className="segmented">{(["day", "week", "month", "quarter"] as TimeGrain[]).map((value) => <button key={value} type="button" aria-pressed={grain === value} className={grain === value ? "active" : ""} onClick={() => setGrain(value)}>{GRAIN_LABELS[value]}</button>)}</div></div>
          <div className="control-set"><span>研究区间</span><div className="segmented warm">{(["30d", "90d", "1y", "all"] as Preset[]).map((preset) => <button key={preset} type="button" aria-pressed={mode === preset} className={mode === preset ? "active" : ""} onClick={() => selectPreset(preset)}>{PRESET_LABELS[preset]}</button>)}</div></div>
          <div className="date-control"><label><span>开始</span><input type="date" min={availableStart} max={selectedEnd} value={selectedStart} onChange={(event) => selectDates(event.target.value, selectedEnd)} /></label><i>—</i><label><span>结束</span><input type="date" min={selectedStart} max={availableEnd} value={selectedEnd} onChange={(event) => selectDates(selectedStart, event.target.value)} /></label></div>
        </section>

        <AnalysisPanel analysis={analysis} mode={mode} />

        <section className="kpi-grid">
          <KpiCard label="区间 Token 总量" value={formatCompact(data.headline.tokenTotal)} delta={data.headline.tokenDeltaPct} note="公开路由样本" tone="tokens" />
          <KpiCard label="加权零售价 / 1M" value={formatPrice(data.headline.blendedPricePerMillion)} delta={data.headline.priceDeltaPct} note={`可定价覆盖 ${formatPercent(data.meta.pricedTokenShare * 100, false)}`} tone="price" />
          <KpiCard label="区间估算金额" value={formatCurrency(data.headline.estimatedRetailValue, true)} delta={data.headline.valueDeltaPct} note="牌价情景 · 非收入" tone="value" />
        </section>

        <RangeNavigator points={allDaily} startIndex={startIndex} endIndex={endIndex} onChange={selectIndexes} />
        <EventRail events={snapshot.events} start={rangeStart} end={rangeEnd} />

        <div className="charts-stack">
          {(["tokens", "price", "value"] as ChartMetric[]).map((metric) => <TrendChart key={metric} data={data} metric={metric} events={snapshot.events} hoveredKey={hoveredKey} onHover={setHoveredKey} mode={mode} selectedStart={selectedStart} selectedEnd={selectedEnd} availableStart={availableStart} availableEnd={availableEnd} onSelectPreset={selectPreset} onSelectDates={selectDates} />)}
          <ModelTable data={data} disabled={mode === "custom"} />
        </div>

        <section className="methodology">
          <header><span className="section-code">05 · METHODOLOGY</span><h2>统计口径与可信边界</h2><p>让结论可复核，也让不能从数据中得到的部分保持克制。</p></header>
          <div className="method-grid"><article><span>01</span><strong>需求</strong><p>日、周、月、季度均由页面内置的 UTC 日数据聚合；对比值使用紧邻的前一等长区间。</p></article><article><span>02</span><strong>价格</strong><p>默认按 80% 输入、20% 输出估算混合价，再按模型 Token 结构加权。</p></article><article><span>03</span><strong>金额</strong><p>只计算有公开价格的 Token，未知价格不补值，并明确披露可定价覆盖率。</p></article><article><span>04</span><strong>事件</strong><p>模型发布时间来自厂商官方公告；与曲线同轴展示只提供时间关联，不声明因果。</p></article></div>
          <div className="source-note"><strong>数据边界</strong><ul>{data.meta.notes.slice(1).map((note) => <li key={note}>{note.replace("OpenRouter", "公开路由市场")}</li>)}</ul></div>
        </section>
      </main>

      <footer className="site-footer"><Brand /><p>页面数据与解读在本地生成并随网页版本发布；线上不查询数据、不使用查询密钥、不写入数据库。</p><span>BUILD {snapshot.generatedAt.slice(0, 10).replaceAll("-", ".")}</span></footer>
    </div>
  );
}
